import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

const SETTINGS_DOC = 'settings';
const DEPARTMENT_SETTINGS_ID = 'departmentSettings';

// Cache for department settings
let settingsCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5000; // 5 seconds cache

/**
 * Clear the department settings cache
 */
export const clearDepartmentSettingsCache = () => {
  settingsCache = null;
  cacheTimestamp = null;
  console.log('Department settings cache cleared');
};

/**
 * Get department settings including hiring status and disabled departments
 * @returns {Promise<Object>}
 */
export const getDepartmentSettings = async () => {
  try {
    // Check if cache is valid
    const now = Date.now();
    if (settingsCache && cacheTimestamp && (now - cacheTimestamp) < CACHE_DURATION) {
      console.log('Returning cached department settings');
      return settingsCache;
    }

    console.log('Fetching fresh department settings from Firebase');
    const settingsRef = doc(db, SETTINGS_DOC, DEPARTMENT_SETTINGS_ID);
    const settingsDoc = await getDoc(settingsRef);

    if (settingsDoc.exists()) {
      settingsCache = settingsDoc.data();
      cacheTimestamp = now;
      console.log('Fetched settings:', settingsCache);
      return settingsCache;
    }

    // Create default settings if document doesn't exist
    const defaultSettings = {
      isHiringEnabled: true,
      showHiringNotification: false,
      showIndustrialVisitNotification: true,
      emailConfirmationEnabled: true,
      disabledDepartments: {},
      imcDayRegistrationStatus: {
        'Quiz Competition': true,
        'Web Design Competition': true,
        'Techno Sapiens': false,
        'Industrial Visit': false,
        'Orbit 1.0': true,
        'Orientation 2026': false,
        'Ducat Educational Visit': false
      },
      eventCapacity: 55,
      eventCapacities: {
        'Quiz Competition': 55,
        'Web Design Competition': 55,
        'Techno Sapiens': 55,
        'Industrial Visit': 55,
        'Orbit 1.0': 100,
        'Orientation 2026': 100,
        'Ducat Educational Visit': 55
      }
    };
    // Persist defaults to DB for consistency across Admin and UI
    await setDoc(settingsRef, defaultSettings, { merge: true });

    settingsCache = defaultSettings;
    cacheTimestamp = now;
    console.log('Using default settings:', defaultSettings);
    return defaultSettings;
  } catch (error) {
    console.error('Error fetching department settings:', error);
    throw error;
  }
};

/**
 * Get capacity for a specific event day, falling back to global eventCapacity
 * @param {string} dayName
 * @returns {Promise<number>}
 */
export const getEventCapacityFor = async (dayName) => {
  try {
    const settingsRef = doc(db, SETTINGS_DOC, DEPARTMENT_SETTINGS_ID);
    const settingsDoc = await getDoc(settingsRef);
    if (settingsDoc.exists()) {
      const data = settingsDoc.data();
      const map = data.eventCapacities || {};
      return map[dayName] || data.eventCapacity || 55;
    }
    return 55;
  } catch (e) {
    return 55;
  }
};

/**
 * Update capacity for a specific event day (writes eventCapacities[dayName])
 * @param {string} dayName
 * @param {number} capacity
 */
export const updateEventCapacityFor = async (dayName, capacity) => {
  try {
    const settingsRef = doc(db, SETTINGS_DOC, DEPARTMENT_SETTINGS_ID);
    const currentDoc = await getDoc(settingsRef);
    const current = currentDoc.exists() ? currentDoc.data() : {};
    const eventCapacities = { ...(current.eventCapacities || {}) };
    eventCapacities[dayName] = capacity;
    await setDoc(settingsRef, { eventCapacities }, { merge: true });
    clearDepartmentSettingsCache();
    return capacity;
  } catch (error) {
    throw new Error('Failed to update event capacity for ' + dayName + ': ' + error.message);
  }
};

/**
 * Update event capacity
 * @param {number} capacity - New capacity value
 */
export const updateEventCapacity = async (capacity) => {
  try {
    console.log('=== UPDATE EVENT CAPACITY START ===');
    console.log('New capacity value:', capacity);

    const settingsRef = doc(db, SETTINGS_DOC, DEPARTMENT_SETTINGS_ID);

    // First, verify current data
    const currentDoc = await getDoc(settingsRef);
    console.log('Current settings before update:', currentDoc.exists() ? currentDoc.data() : 'Document does not exist');

    // Update the capacity
    await setDoc(settingsRef, {
      eventCapacity: capacity
    }, { merge: true });

    console.log('Firebase setDoc completed');

    // Verify the update
    const updatedDoc = await getDoc(settingsRef);
    const updatedData = updatedDoc.data();
    console.log('Updated settings after update:', updatedData);
    console.log('Verified capacity in Firebase:', updatedData.eventCapacity);

    // Clear cache to force fresh fetch
    clearDepartmentSettingsCache();

    console.log('=== UPDATE EVENT CAPACITY END ===');

    if (updatedData.eventCapacity !== capacity) {
      throw new Error(`Capacity update verification failed. Expected ${capacity}, got ${updatedData.eventCapacity}`);
    }

    return updatedData.eventCapacity;
  } catch (error) {
    console.error('!!! ERROR UPDATING EVENT CAPACITY !!!');
    console.error('Error details:', error);
    throw new Error('Failed to update event capacity: ' + error.message);
  }
};

/**
 * Update hiring status
 * @param {boolean} isEnabled
 */
export const updateHiringStatus = async (isEnabled) => {
  try {
    const settingsRef = doc(db, SETTINGS_DOC, DEPARTMENT_SETTINGS_ID);
    await setDoc(settingsRef, {
      isHiringEnabled: isEnabled
    }, { merge: true });
    clearDepartmentSettingsCache();
  } catch (error) {
    console.error('Error updating hiring status:', error);
    throw error;
  }
};

/**
 * Update hiring notification status
 * @param {boolean} showNotification
 */
export const updateHiringNotificationStatus = async (showNotification) => {
  try {
    const settingsRef = doc(db, SETTINGS_DOC, DEPARTMENT_SETTINGS_ID);
    await setDoc(settingsRef, {
      showHiringNotification: showNotification
    }, { merge: true });
    clearDepartmentSettingsCache();
  } catch (error) {
    console.error('Error updating hiring notification status:', error);
    throw error;
  }
};

/**
 * Update industrial visit notification status
 * @param {boolean} showNotification
 */
export const updateIndustrialVisitNotificationStatus = async (showNotification) => {
  try {
    const settingsRef = doc(db, SETTINGS_DOC, DEPARTMENT_SETTINGS_ID);
    await setDoc(settingsRef, {
      showIndustrialVisitNotification: showNotification
    }, { merge: true });
    clearDepartmentSettingsCache();
  } catch (error) {
    console.error('Error updating industrial visit notification status:', error);
    throw error;
  }
};

/**
 * Update email confirmation status
 * @param {boolean} isEnabled
 */
export const updateEmailConfirmationStatus = async (isEnabled) => {
  try {
    const settingsRef = doc(db, SETTINGS_DOC, DEPARTMENT_SETTINGS_ID);
    await setDoc(settingsRef, {
      emailConfirmationEnabled: isEnabled
    }, { merge: true });
    clearDepartmentSettingsCache();
  } catch (error) {
    console.error('Error updating email confirmation status:', error);
    throw error;
  }
};

/**
 * Update department status (enabled/disabled)
 * @param {string} department
 * @param {boolean} isDisabled
 */
export const updateDepartmentStatus = async (department, isDisabled) => {
  try {
    const settingsRef = doc(db, SETTINGS_DOC, DEPARTMENT_SETTINGS_ID);
    const settingsDoc = await getDoc(settingsRef);

    let disabledDepartments = {};
    if (settingsDoc.exists()) {
      disabledDepartments = settingsDoc.data().disabledDepartments || {};
    }

    disabledDepartments[department] = isDisabled;

    await setDoc(settingsRef, {
      disabledDepartments
    }, { merge: true });
    clearDepartmentSettingsCache();
  } catch (error) {
    console.error('Error updating department status:', error);
    throw error;
  }
};

/**
 * Update IMC day registration status
 * @param {string} dayName - The event day name
 * @param {boolean} isOpen - Whether registration is open
 */
export const updateIMCDayRegistrationStatus = async (dayName, isOpen) => {
  try {
    const settingsRef = doc(db, SETTINGS_DOC, DEPARTMENT_SETTINGS_ID);
    const settingsDoc = await getDoc(settingsRef);

    let imcDayRegistrationStatus = {
      'Quiz Competition': true,
      'Web Design Competition': true,
      'Techno Sapiens': false,
      'Industrial Visit': false,
      'Orbit 1.0': true,
      'Orientation 2026': false,
      'Ducat Educational Visit': false
    };

    if (settingsDoc.exists()) {
      imcDayRegistrationStatus = settingsDoc.data().imcDayRegistrationStatus || imcDayRegistrationStatus;
    }

    imcDayRegistrationStatus[dayName] = isOpen;

    await setDoc(settingsRef, {
      imcDayRegistrationStatus
    }, { merge: true });
    clearDepartmentSettingsCache();
  } catch (error) {
    console.error('Error updating IMC day registration status:', error);
    throw error;
  }
};

/**
 * Update all department settings at once
 * @param {Object} settings - Complete settings object
 */
export const updateAllDepartmentSettings = async (settings) => {
  try {
    const settingsRef = doc(db, SETTINGS_DOC, DEPARTMENT_SETTINGS_ID);
    await setDoc(settingsRef, settings, { merge: true });
    clearDepartmentSettingsCache();
  } catch (error) {
    console.error('Error updating all department settings:', error);
    throw error;
  }
};